import { useNavigate } from "react-router-dom";
import { pagesForSources } from "@prism/shared/modules";
import { Card, CardHeader, CardBody, PageError, PageLoading } from "../components/index.js";
import { useApi } from "../hooks/useApi.js";

/**
 * Client account "Home" — the relationship view a CLIENT (or legacy OWNER)
 * user lands on at /home. Surfaces the data BMG already holds about the
 * engagement (contracted services, goals, managed channels, account team,
 * key contacts) from the ClientKnowledge table. No Windsor connection
 * required — every section is conditional, so a sparsely-filled client
 * simply renders fewer cards rather than empty shells.
 *
 * Backend: GET /api/dashboard/home, scoped to req.clientId.
 */

// Map a knowledge social-channel key to its human label. Order here is the
// render order of the "Channels We Manage" list.
const SOCIAL_LABELS = {
  facebookUrl:  "Facebook",
  instagramUrl: "Instagram",
  linkedinUrl:  "LinkedIn",
  gbpUrl:       "Google Business",
  tiktokUrl:    "TikTok",
  youtubeUrl:   "YouTube",
};

// Translate a BMG role into the label a client should see. EMPLOYEEs carry an
// explicit jobTitle; SUPER_ADMIN/ADMIN have none, so present them as the
// account's director rather than leaking the internal role enum.
function teamRoleLabel(member) {
  if (member.jobTitle) return member.jobTitle;
  if (member.role === "SUPER_ADMIN" || member.role === "ADMIN") return "Account Director";
  return null;
}

// A 6-digit hex accent at ~10% opacity for the header tint. Falls back to a
// neutral tint if the stored accent isn't a plain #rrggbb value.
function accentTint(hex) {
  return /^#[0-9a-fA-F]{6}$/.test(hex || "") ? `${hex}1a` : "rgba(0,0,0,0.04)";
}

export function ClientHome() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApi("/api/dashboard/home");

  if (error) return <PageError error={error} onRetry={() => reload()} />;
  if (loading || !data) return <PageLoading />;

  const {
    client, serviceTypes, contractedServices, contractRenewalDate, goals,
    postAllocation, socialChannels = {}, contacts = [], team = [],
    connectedSourceTypes = [],
  } = data;

  const accent = client?.accentColor || "#ff6804";
  const initials = (client?.name || "?").trim().charAt(0).toUpperCase();
  const serviceTags = (serviceTypes || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const socialEntries = Object.entries(socialChannels).filter(
    ([key, url]) => SOCIAL_LABELS[key] && url
  );

  // Quick links: the dashboard tabs this client's connected sources enable,
  // excluding the settings group. Empty when nothing is connected yet.
  const quickLinks = pagesForSources(connectedSourceTypes).filter(
    (p) => p.group === "main"
  );

  return (
    <div className="max-w-[1100px] space-y-4">
      {/* Section 1 — account header */}
      <div
        className="rounded-card border border-border-default px-5 py-5"
        style={{ backgroundColor: accentTint(accent), borderLeft: `3px solid ${accent}` }}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3.5 min-w-0">
            {client?.logoUrl ? (
              <img
                src={client.logoUrl}
                alt=""
                className="w-12 h-12 rounded-lg object-contain bg-surface border border-border-default flex-shrink-0"
              />
            ) : (
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-semibold text-[18px] flex-shrink-0"
                style={{ backgroundColor: accent }}
                aria-hidden="true"
              >
                {initials}
              </div>
            )}
            <div className="min-w-0">
              <h2 className="text-[20px] font-semibold text-text-primary leading-tight truncate">
                {client?.name}
              </h2>
              <p className="text-[12px] text-text-muted mt-0.5">
                {[client?.industry, client?.location].filter(Boolean).join(" · ")}
              </p>
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-text-secondary bg-surface border border-border-default rounded-full px-2.5 py-1">
              <span className="w-2 h-2 rounded-full bg-positive" />
              Active
            </span>
            {serviceTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 justify-end">
                {serviceTags.map((tag) => (
                  <span
                    key={tag}
                    className="text-[10px] uppercase tracking-wide text-text-secondary bg-surface border border-border-default rounded px-1.5 py-0.5"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section 2 — two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Left column (~60%) */}
        <div className="lg:col-span-3 space-y-4">
          {contractedServices && (
            <Card>
              <CardHeader title="What We're Delivering" />
              <CardBody>
                <div className="text-[13px] text-text-primary whitespace-pre-wrap leading-relaxed">
                  {contractedServices}
                </div>
                {contractRenewalDate && (
                  <div className="text-[11px] text-text-muted mt-4 pt-3 border-t border-border-light">
                    Contract renews: {contractRenewalDate}
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {goals && (
            <Card>
              <CardHeader title="Your Goals" />
              <CardBody>
                <div className="text-[13px] text-text-primary whitespace-pre-wrap leading-relaxed">
                  {goals}
                </div>
              </CardBody>
            </Card>
          )}
        </div>

        {/* Right column (~40%) */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader title="Your BMG Team" />
            <CardBody>
              {team.length === 0 ? (
                <div className="text-[13px] text-text-muted">
                  Your account team will be listed here soon.
                </div>
              ) : (
                <ul className="space-y-3.5">
                  {team.map((member) => {
                    const roleLabel = teamRoleLabel(member);
                    return (
                      <li key={member.id}>
                        <div className="text-[13px] font-semibold text-text-primary leading-tight">
                          {member.name}
                        </div>
                        {roleLabel && (
                          <div className="text-[11px] text-text-muted mt-0.5">{roleLabel}</div>
                        )}
                        <a
                          href={`mailto:${member.email}`}
                          className="text-[12px] text-accent hover:underline"
                        >
                          {member.email}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardBody>
          </Card>

          {socialEntries.length > 0 && (
            <Card>
              <CardHeader title="Channels We Manage" />
              <CardBody>
                <ul className="space-y-2.5">
                  {socialEntries.map(([key, url]) => (
                    <li key={key} className="flex items-center justify-between gap-3">
                      <span className="text-[12px] font-medium text-text-secondary">
                        {SOCIAL_LABELS[key]}
                      </span>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[12px] text-accent hover:underline truncate"
                      >
                        Visit →
                      </a>
                    </li>
                  ))}
                </ul>
                {postAllocation && (
                  <div className="text-[11px] text-text-muted mt-4 pt-3 border-t border-border-light">
                    Post frequency: {postAllocation}
                  </div>
                )}
              </CardBody>
            </Card>
          )}
        </div>
      </div>

      {/* Section 3 — key contacts */}
      {contacts.length > 0 && (
        <Card>
          <CardHeader title="Key Contacts" />
          <CardBody>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {contacts.map((c, i) => (
                <div
                  key={i}
                  className="border border-border-light rounded-lg px-4 py-3"
                >
                  {c.name && (
                    <div className="text-[13px] font-semibold text-text-primary leading-tight">
                      {c.name}
                    </div>
                  )}
                  {c.role && (
                    <div className="text-[11px] text-text-muted mt-0.5">{c.role}</div>
                  )}
                  {c.email && (
                    <a
                      href={`mailto:${c.email}`}
                      className="block text-[12px] text-accent hover:underline mt-1.5 truncate"
                    >
                      {c.email}
                    </a>
                  )}
                  {c.phone && (
                    <a
                      href={`tel:${c.phone}`}
                      className="block text-[12px] text-text-secondary hover:underline mt-0.5"
                    >
                      {c.phone}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Section 4 — quick links into the live dashboard */}
      {quickLinks.length > 0 && (
        <Card>
          <CardBody className="flex items-center justify-between gap-4 flex-wrap">
            <div className="text-[13px] font-medium text-text-primary">
              See how your marketing is performing →
            </div>
            <div className="flex flex-wrap gap-1.5">
              {quickLinks.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => navigate(`/${p.key}`)}
                  className="text-[12px] font-medium text-accent bg-accent-bg border border-accent-border rounded px-2.5 py-1 hover:underline"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
